import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { Order, OrderStatus } from '../lib/types';
import { ORDER_STATUSES, STATUS_LABELS, STATUS_COLORS } from '../lib/types';
import { listOrders, createOrder, updateOrder } from '../lib/api';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const COMPLETED_DAYS_OPTIONS = [7, 14, 30, 60, 90];

// Where an order came from, plus that platform's own reference number. The
// shared identifier is always order.orderId (the KV key); the platform ref is
// secondary — handy for looking the order up on Amazon Seller / WooCommerce.
function platformInfo(order: Order): { label: string; ref?: string; badge: string } {
  switch (order.source) {
    case 'amazon':
      return { label: 'Amazon', ref: order.amazonOrderId, badge: 'bg-orange-100 text-orange-800' };
    case 'woocommerce':
    case 'web':
      return { label: 'Website', ref: order.wooOrderId, badge: 'bg-emerald-100 text-emerald-800' };
    case 'manual':
      return { label: 'Manual', ref: order.amazonOrderId, badge: 'bg-gray-100 text-gray-700' };
    default:
      // Older orders predate the `source` field — infer from what they carry.
      if (order.amazonOrderId) return { label: 'Amazon', ref: order.amazonOrderId, badge: 'bg-orange-100 text-orange-800' };
      if (order.wooOrderId) return { label: 'Website', ref: order.wooOrderId, badge: 'bg-emerald-100 text-emerald-800' };
      return { label: 'Manual', badge: 'bg-gray-100 text-gray-700' };
  }
}

// Per-tab cache of the last list response per filter combination. Purely a
// perceived-latency aid: every page view still re-fetches and overwrites it.
function readOrdersCache(key: string): Order[] | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Order[]) : null;
  } catch {
    return null;
  }
}

function writeOrdersCache(key: string, orders: Order[]): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(orders));
  } catch {
    // Quota or private-mode failure: silently fall back to network-only.
  }
}

export function OrderList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [completedDays, setCompletedDays] = useState(30);
  const [savingStatusOrderId, setSavingStatusOrderId] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, [statusFilter, completedDays]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, pageSize]);

  async function loadOrders() {
    const cacheKey = `orders-list:${statusFilter || 'all'}:${completedDays}`;

    // Stale-while-revalidate: paint the last result for this filter
    // immediately (per-tab sessionStorage), then refresh from the API.
    const cached = readOrdersCache(cacheKey);
    if (cached) {
      setOrders(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await listOrders({
        status: statusFilter || undefined,
        view: 'active',
        completedDays,
      });
      setOrders(data.orders);
      writeOrdersCache(cacheKey, data.orders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(orderId: string, newStatus: OrderStatus) {
    setSavingStatusOrderId(orderId);
    setError(null);
    try {
      const result = await updateOrder(orderId, { status: newStatus });
      setOrders(prev => prev.map(o => o.orderId === orderId ? result.order : o));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSavingStatusOrderId(null);
    }
  }

  // Helper to display item names
  function getItemNames(order: Order): string[] {
    if (!order.items || order.items.length === 0) return ['—'];
    return order.items.map(i => i.name || '(unnamed)');
  }

  // Filter orders by search query (client-side)
  const filteredOrders = orders.filter(order => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();

    // Search in our shared order ID (the KV key)
    if (order.orderId?.toLowerCase().includes(query)) return true;

    // Search in platform reference IDs (Amazon / WooCommerce)
    if (order.amazonOrderId?.toLowerCase().includes(query)) return true;
    if (order.wooOrderId?.toLowerCase().includes(query)) return true;

    // Search in buyer name
    if (order.buyerName?.toLowerCase().includes(query)) return true;

    // Search in item names
    if (order.items?.some(item => item.name?.toLowerCase().includes(query))) return true;

    // Search in item IDs
    if (order.items?.some(item => item.amazonOrderItemId?.toLowerCase().includes(query))) return true;

    return false;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

  return (
    <div>
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-900">Orders</h1>
        <button
          onClick={() => setShowNewForm(true)}
          class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          New Order
        </button>
      </div>

      {/* Filters */}
      <div class="mb-4 flex flex-wrap gap-4 items-center">
          <div class="flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              value={searchQuery}
              onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
              placeholder="Search by name, order ID, or item..."
              class="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}
            class="border rounded-lg px-3 py-2"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={completedDays}
            onChange={(e) => setCompletedDays(Number((e.target as HTMLSelectElement).value))}
            class="border rounded-lg px-3 py-2"
            title="Show completed orders from"
          >
            {COMPLETED_DAYS_OPTIONS.map(days => (
              <option key={days} value={days}>Last {days} days</option>
            ))}
          </select>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number((e.target as HTMLSelectElement).value))}
            class="border rounded-lg px-3 py-2"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size} per page</option>
            ))}
          </select>
      </div>

      {/* Results count */}
      {!loading && (
        <div class="mb-2 text-sm text-gray-500">
          Showing {filteredOrders.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, filteredOrders.length)} of {filteredOrders.length} active orders
          {searchQuery && ` matching "${searchQuery}"`}
          {' '}(includes completed orders from last {completedDays} days)
        </div>
      )}

      {/* Error message */}
      {error && (
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div class="text-center py-8 text-gray-500">Loading orders...</div>
      )}

      {/* Active Orders list */}
      {!loading && filteredOrders.length === 0 && (
        <div class="text-center py-8 text-gray-500">
          {orders.length === 0
            ? 'No active orders found. Create one to get started.'
            : `No orders match "${searchQuery}"`}
        </div>
      )}

      {!loading && filteredOrders.length > 0 && (
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Order
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Items
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              {paginatedOrders.map((order) => (
                <tr
                  key={order.orderId}
                  onClick={() => route(`/orders/${order.orderId}`)}
                  class={`cursor-pointer ${order.cancellationRequestedAt && order.status !== 'CANCELLED' ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                >
                  <td class="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-700" title={order.orderId}>
                    {order.orderId}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    {(() => {
                      const p = platformInfo(order);
                      return (
                        <div>
                          <span class={`px-2 py-0.5 text-xs font-medium rounded-full ${p.badge}`}>
                            {p.label}
                          </span>
                          {p.ref && (
                            <div class="mt-1 text-xs font-mono text-gray-400" title={p.ref}>{p.ref}</div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {getItemNames(order).map((name, i) => (
                      <div key={i}>{name}</div>
                    ))}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.orderId, (e.target as HTMLSelectElement).value as OrderStatus)}
                      disabled={savingStatusOrderId === order.orderId}
                      class={`px-2 py-1 text-xs rounded-full border-0 cursor-pointer ${STATUS_COLORS[order.status]} ${savingStatusOrderId === order.orderId ? 'opacity-50' : ''}`}
                    >
                      {ORDER_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </option>
                      ))}
                    </select>
                    {order.cancellationRequestedAt && order.status !== 'CANCELLED' && (
                      <div
                        class="mt-1 text-xs font-medium text-amber-700"
                        title={`Cancelled on the website ${new Date(order.cancellationRequestedAt).toLocaleString()} while already in production — review and cancel manually if appropriate.`}
                      >
                        ⚠ Cancel requested
                      </div>
                    )}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div class="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
              </div>

              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              <div class="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New order form modal */}
      {showNewForm && (
        <NewOrderModal
          onClose={() => setShowNewForm(false)}
          onCreated={(order) => {
            setShowNewForm(false);
            route(`/orders/${order.orderId}`);
          }}
        />
      )}
    </div>
  );
}

interface ItemFormData {
  name: string;
  pronouns: string;
  frameMaterial: 'cherry' | 'sapele';
  needsProof: boolean;
  culturalContext: string;
  notes: string;
  amazonOrderItemId: string;
}

function NewOrderModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (order: Order) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buyerName, setBuyerName] = useState('');
  const [amazonOrderId, setAmazonOrderId] = useState('');
  const [items, setItems] = useState<ItemFormData[]>([
    { name: '', pronouns: 'they/them', frameMaterial: 'sapele', needsProof: false, culturalContext: '', notes: '', amazonOrderItemId: '' }
  ]);

  function updateItem(index: number, field: keyof ItemFormData, value: string | boolean) {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  }

  function addItem() {
    setItems(prev => [...prev, { name: '', pronouns: 'they/them', frameMaterial: 'sapele', needsProof: false, culturalContext: '', notes: '', amazonOrderItemId: '' }]);
  }

  function removeItem(index: number) {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index));
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate
    if (!buyerName.trim()) {
      setError('Buyer name is required');
      setSubmitting(false);
      return;
    }

    const validItems = items.filter(item => item.name.trim());
    if (validItems.length === 0) {
      setError('At least one item with a name is required');
      setSubmitting(false);
      return;
    }

    try {
      const result = await createOrder({
        buyerName: buyerName.trim(),
        amazonOrderId: amazonOrderId.trim() || undefined,
        items: validItems.map(item => ({
          name: item.name.trim(),
          pronouns: item.pronouns,
          frameMaterial: item.frameMaterial,
          needsProof: item.needsProof,
          culturalContext: item.culturalContext.trim() || undefined,
          notes: item.notes.trim() || undefined,
          amazonOrderItemId: item.amazonOrderItemId.trim() || undefined,
        })),
      });
      onCreated(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-semibold">New Order</h2>
            <button
              onClick={onClose}
              class="text-gray-400 hover:text-gray-600 text-2xl"
            >
              &times;
            </button>
          </div>

          {error && (
            <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} class="space-y-4">
            {/* Order-level fields */}
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Buyer Name *
                </label>
                <input
                  type="text"
                  value={buyerName}
                  onInput={(e) => setBuyerName((e.target as HTMLInputElement).value)}
                  class="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Amazon Order ID
                </label>
                <input
                  type="text"
                  value={amazonOrderId}
                  onInput={(e) => setAmazonOrderId((e.target as HTMLInputElement).value)}
                  placeholder="e.g., 114-5751193-3802629"
                  class="w-full border rounded-lg px-3 py-2 font-mono text-sm"
                />
              </div>
            </div>

            {/* Items */}
            <div class="border-t pt-4 mt-4">
              <div class="flex justify-between items-center mb-3">
                <h3 class="font-medium text-gray-900">Items ({items.length})</h3>
                <button
                  type="button"
                  onClick={addItem}
                  class="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Item
                </button>
              </div>

              <div class="space-y-4">
                {items.map((item, index) => (
                  <div key={index} class="border rounded-lg p-4 bg-gray-50">
                    <div class="flex justify-between items-start mb-3">
                      <span class="text-sm font-medium text-gray-600">Item {index + 1}</span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          class="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Name *</label>
                        <input
                          type="text"
                          value={item.name}
                          onInput={(e) => updateItem(index, 'name', (e.target as HTMLInputElement).value)}
                          class="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>

                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Pronouns *</label>
                        <select
                          value={item.pronouns}
                          onChange={(e) => updateItem(index, 'pronouns', (e.target as HTMLSelectElement).value)}
                          class="w-full border rounded px-2 py-1.5 text-sm"
                        >
                          <option value="he/him">he/him</option>
                          <option value="she/her">she/her</option>
                          <option value="they/them">they/them</option>
                        </select>
                      </div>

                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Frame Material *</label>
                        <select
                          value={item.frameMaterial}
                          onChange={(e) => updateItem(index, 'frameMaterial', (e.target as HTMLSelectElement).value)}
                          class="w-full border rounded px-2 py-1.5 text-sm"
                        >
                          <option value="cherry">Cherry</option>
                          <option value="sapele">Sapele</option>
                        </select>
                      </div>

                      <div class="flex items-end pb-1">
                        <div class="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`needsProof-${index}`}
                            checked={item.needsProof}
                            onChange={(e) => updateItem(index, 'needsProof', (e.target as HTMLInputElement).checked)}
                          />
                          <label for={`needsProof-${index}`} class="text-xs text-gray-500">
                            Needs proof
                          </label>
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Cultural Context</label>
                        <input
                          type="text"
                          value={item.culturalContext}
                          onInput={(e) => updateItem(index, 'culturalContext', (e.target as HTMLInputElement).value)}
                          placeholder="e.g., Irish, Japanese"
                          class="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>

                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Order Item ID</label>
                        <input
                          type="text"
                          value={item.amazonOrderItemId}
                          onInput={(e) => updateItem(index, 'amazonOrderItemId', (e.target as HTMLInputElement).value)}
                          placeholder="e.g., 15252699852481"
                          class="w-full border rounded px-2 py-1.5 text-sm font-mono"
                        />
                      </div>

                      <div class="col-span-2">
                        <label class="block text-xs text-gray-500 mb-1">Notes</label>
                        <input
                          type="text"
                          value={item.notes}
                          onInput={(e) => updateItem(index, 'notes', (e.target as HTMLInputElement).value)}
                          class="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                class="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Creating...' : `Create Order (${items.filter(i => i.name.trim()).length} items)`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
